// Free plan vs teacher plan in the teacher workspace (D-090). Pure helpers so the gates can be
// tested without React; the server enforces the same rules (src/plan-policy.ts, src/index.ts).
import { FREE_PLAN_LIMITS, FREE_SCENE_ID, FREE_THEME_ID, planTierFor, type AccessState, type PlanLevel, type PlanTier } from "../../src/plan-policy";
import type { ToolName } from "./tool-navigation";

export type PlanAccess = {
  state: string;
  workspaceState?: string;
  endsAt: string | null;
  plan?: PlanTier;
  freeLimits?: { activeClassLimit: number; studentsPerClassLimit: number };
  overLimit?: OverLimit | null;
  /** D-114: free, basic (teacher plan) or plus, and the class/student limits that apply. */
  level?: PlanLevel;
  limits?: { activeClassLimit: number; studentsPerClassLimit: number };
  /** The running access comes from referral reward time, not a trial or a purchase. */
  reward?: boolean;
  /** Add-ons were retired in D-114; always false. */
  addonsAvailable?: boolean;
};
/** Why a free-plan class is paused: it is not the oldest active class, and/or it has too many students. */
export type OverLimit = { notOldestClass: boolean; extraStudents: number };

const ACCESS_STATES: readonly string[] = ["trial", "active", "free", "over_limit", "read_only", "suspended"];

/** Plan of the class's workspace. Older snapshots have no `plan`; derive it from the workspace state. */
export function planOf(access: PlanAccess): PlanTier {
  if (access.plan === "free" || access.plan === "teacher") return access.plan;
  const state = access.workspaceState ?? access.state;
  return ACCESS_STATES.includes(state) ? planTierFor(state as AccessState) : "free";
}
export const isTeacherPlan = (access: PlanAccess) => planOf(access) === "teacher";
export const levelOf = (access: PlanAccess): PlanLevel => access.level ?? (planOf(access) === "teacher" ? "basic" : "free");
export const freeLimitsOf = (access: PlanAccess) => access.freeLimits ?? FREE_PLAN_LIMITS;
/** The server's reason for an `over_limit` class; older snapshots only tell us the student count. */
export function overLimitOf(access: PlanAccess, activeStudents: number): OverLimit {
  if (access.overLimit) return access.overLimit;
  const extraStudents = Math.max(0, activeStudents - freeLimitsOf(access).studentsPerClassLimit);
  return { notOldestClass: extraStudents === 0, extraStudents };
}

/** Scoring and editing are open on a trial, a paid plan and a free-plan class within the free limits. */
export const isWritableState = (state: string) => state === "trial" || state === "active" || state === "free";
/** A free-plan class beyond the limit can still archive students (and the teacher can archive classes). */
export const canTidyClass = (state: string, archived: boolean) => !archived && (isWritableState(state) || state === "over_limit");
/** Creating, archiving or restoring classes: everything except a suspended workspace. */
export const canManageClasses = (workspaceState: string) => workspaceState !== "suspended";

/** Whole days left until `endsAt` (UTC "YYYY-MM-DD HH:MM:SS" as the Worker stores it), never negative. */
export function daysLeft(endsAt: string | null, now = Date.now()): number | null {
  if (!endsAt) return null;
  const iso = endsAt.includes("T") ? endsAt
    : /^\d{4}-\d{2}-\d{2}$/.test(endsAt) ? `${endsAt}T00:00:00Z` : `${endsAt.replace(" ", "T")}Z`;
  const end = Date.parse(iso);
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - now) / 86_400_000));
}

/** Dashboard tools that need the teacher plan, with the free public tools that do the same job. */
export const TEACHER_PLAN_TOOLS: Partial<Record<ToolName, readonly string[]>> = {
  games: ["/tools/times-tables", "/tools/charades-cards", "/tools/guess-number", "/tools/1a2b", "/tools/mental-math", "/tools/tic-tac-toe", "/tools/bingo-cards", "/tools/schulte-table", "/tools/tower-of-hanoi", "/tools/24-game", "/tools/kk-phonics", "/tools/alphabet-chart"],
  comments: ["/tools/report-comments", "/tools/grade-stats"],
  certificates: ["/tools/certificate-maker"],
  seating: ["/tools/seating-chart"],
};
export const toolLocked = (tool: ToolName, plan: PlanTier) => plan === "free" && tool in TEACHER_PLAN_TOOLS;

/** On the free plan only the forest theme and scene can be chosen; a saved paid one stays shown. */
export const themeLocked = (id: string, plan: PlanTier) => plan === "free" && id !== FREE_THEME_ID;
export const sceneLocked = (id: string, plan: PlanTier) => plan === "free" && id !== FREE_SCENE_ID;

/** Server error codes that mean "this needs the teacher plan or more room". */
export const PLAN_ERROR_CODES = ["teacher_plan_required", "free_plan_limit", "class_quota_exceeded", "student_quota_exceeded"] as const;
export type PlanErrorCode = typeof PLAN_ERROR_CODES[number];
export const isPlanErrorCode = (code: unknown): code is PlanErrorCode =>
  typeof code === "string" && (PLAN_ERROR_CODES as readonly string[]).includes(code);
/** Window event the api() helper raises for these errors, so the workspace can offer the upgrade. */
export const PLAN_ERROR_EVENT = "classmori:plan-error";
export type PlanErrorDetail = { code: PlanErrorCode; message: string };
